import { Button, Vector2, VisualizerProject, Visualizers } from "@app/shared";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { emptyGuid } from "../../../helpers";
import { Spinner } from "../../shared";
import { DataViewer, DataViewType } from "./DataViewer";
import { ProjectScrubber } from "./ProjectScrubber";
import { hasChangesToProject, loadProject, PROJECT_RUNTIME_ONLY_KEYS, ProjectWithData } from "./ProjectWithData";

export enum ViewPaneLayout {
    Pane_1,
    Pane_2,
    Pane_3
};

type ProjectViewProps = {
    projectId: string;
    onClose?: () => void;
    layout: ViewPaneLayout;
};

export const ProjectView: React.FC<ProjectViewProps> = (props) => {
    const [project, setProject] = useState<ProjectWithData>();

    // State variables to update UI
    const [isScrubbing, setIsScrubbing] = useState(false);
    const [currentTimeS, setCurrentTimeS] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [needsVideoClick, setNeedsVideoClick] = useState(false);
    const [lastVideoClick, setLastVideoClick] = useState<Vector2>();
    const [reloadProject, setReloadProject] = useState(1);
    const [loadingProjectMessage, setLoadingProjectMessage] = useState<string>();

    const handleSetProject = useCallback<React.Dispatch<React.SetStateAction<Partial<ProjectWithData>>>>((changedProject) => {
        //TODO: Maybe check if anything has changed that we can persist to the DB so we can show a save button?
        console.log("Calling update project");
        if (!changedProject) return;

        //Should we check for changes?
        // if (hasChanges === undefined) {
        //{
            var projectA: Partial<ProjectWithData> | undefined;
            var projectB: Partial<ProjectWithData> | undefined;

            if (typeof changedProject === "function") {
                projectA = project;
                projectB = Object.assign(new ProjectWithData(), project);
                projectB = changedProject(projectB);
            } else {
                projectA = project;
                projectB = changedProject;
            }


            if (projectA && projectB && hasChangesToProject(projectA, projectB)) {
                setHasChanges(true);
            }
        //} /*else if (hasChanges) {
            setHasChanges(true);
        //}*/

        setProject(changedProject as any);

    }, [setProject, project, setHasChanges]);

    // Handler to update the current frame (called by the child)
    const handleFrameChange = useCallback((timeS: number) => {
        //If time changes and we are waiting for a click then cancel it
        if (needsVideoClick) {
            setNeedsVideoClick(false);
        }

        if (timeS !== currentTimeS && !isScrubbing) {
            setCurrentTimeS(timeS); //Only update if the time has changed
        }
    }, [setCurrentTimeS, isScrubbing, needsVideoClick, setNeedsVideoClick, currentTimeS]);

    //If the parent changes our project ID, we will need to load again
    useEffect(() => {
        console.log("Project ID changed", props.projectId);
        if (!props.projectId || (props.projectId + "") === emptyGuid) return;
        const abortController = new AbortController();
        const performLoad = async () => {
            const setMessageWithAbort = (message?: string) => {
                if (abortController.signal.aborted) {
                    throw new Error("Loading aborted");
                }
                setLoadingProjectMessage(message);
            };
            try {
                var proj = await loadProject(props.projectId, abortController.signal, setMessageWithAbort);
                if (!abortController.signal.aborted) {
                    setProject(proj);
                }
            } catch (e) { console.log("Error loading project", e); }
        };
        performLoad();
        return () => {
            console.log("Aborting load project");
            abortController.abort();
            setLoadingProjectMessage(undefined);
        }
    }, [props.projectId, setProject, reloadProject, setLoadingProjectMessage]);

    const handleSaveOnClick = async () => {
        if (project) {
            // omit (not pick over Object.keys) — Object.keys skips nullable fields like videoStartDate that have no initializer on a fresh instance.
            const toSave = _.omit(project, [...PROJECT_RUNTIME_ONLY_KEYS, "device"]) as VisualizerProject;
            await Visualizers.saveProject(toSave);
        }
        setReloadProject(prev => (prev + 1));
        setHasChanges(false);
    }

    const dataViewerProps = {
        currentTimeS: currentTimeS,
        isScrubbing: isScrubbing,
        isPlaying: isPlaying,
        onFrameChange: handleFrameChange,
        closeProject: props.onClose,
        setProject: handleSetProject,
        setNeedsVideoClick: setNeedsVideoClick,
        needsVideoClick: needsVideoClick,
        setLastVideoClick: setLastVideoClick,
        lastVideoClick: lastVideoClick,
    };

    return (
        <>
            <div className="flex flex-col gap-3">
                <div className="flex flex-row">
                    {project &&
                        <DataViewer
                            project={project}
                            initialView={DataViewType.Video}
                            {...dataViewerProps}
                        />
                    }
                    {project && (props.layout === ViewPaneLayout.Pane_2 || props.layout === ViewPaneLayout.Pane_3) &&
                        <DataViewer project={project}
                            initialView={DataViewType.Layout}
                            {...dataViewerProps}
                        />
                    }
                    {project && (props.layout === ViewPaneLayout.Pane_3) &&
                        <DataViewer project={project}
                            initialView={DataViewType.Sim_Results}
                            {...dataViewerProps}
                        />
                    }
                </div>
                <ProjectScrubber
                    project={project}
                    setIsScrubbing={setIsScrubbing}
                    currentTimeS={currentTimeS}
                    setCurrentTimeS={setCurrentTimeS}
                    isPlaying={isPlaying}
                    setIsPlaying={setIsPlaying} />
            </div>
            {hasChanges &&
                <div className="absolute right-0 bottom-0 pr-10 pb-5">
                    <Button onClick={handleSaveOnClick}>Save Project</Button>
                </div>
            }
            {loadingProjectMessage &&
                <div className="absolute bottom-1 w-full flex justify-center ">
                    <div className="flex flex-row gap-2 items-center place-content-center justify-items-center">
                        <Spinner /> {loadingProjectMessage}
                    </div>
                </div>
            }
        </>
    );
}